import { useMemo, useState } from 'react';

import { ContractMessage, ContractPriceListMessage, ContractPriceListStatus } from '../../../../types/ContractMessage';
import { Button } from '../../../cdl/Button/Button';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { IconSettings } from '../../../common/icons/cdl/Settings';
import { Box } from '../../../common/ui/Box';
import { ContractAddPriceListsModal } from '../../../contracts/detail/components/ContractAddPriceListsModal';
import { ContractPriceListActivationModal } from '../../../contracts/detail/components/ContractPriceListActivationModal';
import { PriceListRow } from '../../../contracts/detail/components/PriceListRow';
import { PriceListsEmptyState } from '../../../contracts/detail/components/PriceListsEmptyState';
import {
    getInitialPriceListValidFrom,
    getNextPriceListValidFrom,
} from '../../ContractForm/PriceListSection/priceListValidation.helpers';

interface ContractPriceListsSectionProps {
    contract: ContractMessage;
    priceLists: ContractPriceListMessage[];
}

const sortPriceLists = (priceLists: ContractPriceListMessage[]) => {
    return [...priceLists].sort((laterStartingPriceList, earlierStartingPriceList) => {
        const laterStartTimestamp = new Date(laterStartingPriceList.validFrom ?? 0).getTime();
        const earlierStartTimestamp = new Date(earlierStartingPriceList.validFrom ?? 0).getTime();

        return earlierStartTimestamp - laterStartTimestamp;
    });
};

export const ContractPriceListsSection = ({ contract, priceLists }: ContractPriceListsSectionProps) => {
    const role = useRole();
    const [isAddPriceListsModalOpen, setIsAddPriceListsModalOpen] = useState(false);
    const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
    const sortedPriceLists = useMemo(() => sortPriceLists(priceLists), [priceLists]);
    const canManageActivationMode = role.hasAnyAdminRights();
    const customerGroupId = contract.customerGroupId ?? (role.isCustomer() ? role.group.id : undefined);
    const hasAppliedPriceList = priceLists.some(
        (priceList) =>
            priceList.status === ContractPriceListStatus.ACTIVE ||
            (priceList.status === ContractPriceListStatus.PROCESSING && priceList.activeOnApproval) ||
            (priceList.status === ContractPriceListStatus.AWARDED && priceList.activeOnApproval)
    );
    const lastEndedPriceList = priceLists.reduce<ContractPriceListMessage | null>((latestPriceList, priceList) => {
        if (!priceList.validUntil) {
            return latestPriceList;
        }

        if (!latestPriceList?.validUntil) {
            return priceList;
        }

        return new Date(priceList.validUntil).getTime() > new Date(latestPriceList.validUntil).getTime()
            ? priceList
            : latestPriceList;
    }, null);
    const initialValidFrom = getInitialPriceListValidFrom({
        contractValidFrom: contract.validFrom,
        nextAvailableValidFrom: getNextPriceListValidFrom(lastEndedPriceList?.validUntil),
    });

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={8}>
                <Text variant="title" as="h2">
                    {translate('contracts.priceLists.title')}
                </Text>

                {canManageActivationMode ? (
                    <Box display="flex" gap={8}>
                        <Button
                            type="button"
                            LeadingIcon={IconPlus}
                            onClick={() => setIsAddPriceListsModalOpen(true)}
                            disabled={!customerGroupId}
                        >
                            {translate('contracts.priceLists.addPriceList')}
                        </Button>

                        <IconButton
                            type="button"
                            emphasis="medium"
                            Icon={IconSettings}
                            tooltipLabel={translate('contracts.priceLists.activation.open')}
                            onClick={() => setIsActivationModalOpen(true)}
                        />
                    </Box>
                ) : null}
            </Box>

            {sortedPriceLists.length === 0 ? (
                <PriceListsEmptyState title={translate('contracts.priceLists.emptyState.title')} />
            ) : (
                <Box display="flex" flexDirection="column" gap={8} width="100%">
                    {sortedPriceLists.map((priceList) => (
                        <PriceListRow
                            key={priceList.id}
                            contractId={contract.id}
                            customerGroupId={customerGroupId}
                            priceList={priceList}
                            priceListActivationMode={contract.priceListActivationMode}
                        />
                    ))}
                </Box>
            )}

            {isAddPriceListsModalOpen ? (
                <ContractAddPriceListsModal
                    activationMode={contract.priceListActivationMode}
                    contractId={contract.id}
                    customerGroupId={customerGroupId}
                    hasAppliedPriceList={hasAppliedPriceList}
                    initialValidFrom={initialValidFrom}
                    isOpen
                    onDismiss={() => setIsAddPriceListsModalOpen(false)}
                />
            ) : null}

            {isActivationModalOpen ? (
                <ContractPriceListActivationModal
                    contract={contract}
                    isOpen
                    onDismiss={() => setIsActivationModalOpen(false)}
                />
            ) : null}
        </Box>
    );
};
